// src/components/common/ImpositionVisualizer.js
import React from 'react';

const ImpositionVisualizer = ({ 
  impositionData, 
  foldType = null,
  foldOrientation = "vertical", // New prop for fold orientation
  isSaddleStitch = false,
  finalWidth = 0,
  finalHeight = 0,
  unfoldedWidth = 0,   // New prop for unfolded width
  unfoldedHeight = 0,  // New prop for unfolded height
  title = "Imposition Layout",
  foldingDetailsData = null, 
  maxWidth = '400px' 
}) => {
  // Basic validation
  if (!impositionData || !impositionData.usable_width || !impositionData.usable_height || impositionData.items_per_sheet === undefined) {
    return <p className="text-gray-500 text-xs italic">Imposition data incomplete.</p>;
  }

  const {
    items_per_sheet,
    cols,
    rows,
    orientation,
    item_width_with_bleed,
    item_height_with_bleed,
    usable_width, // Use usable_width/height for percentage calculations
    usable_height,
    params: { gutter_mm = 5 } = {} // Default gutter if params missing
  } = impositionData;

  if (items_per_sheet <= 0 || usable_width <= 0 || usable_height <= 0) {
    return <p className="text-gray-500 text-xs italic">No items fit or invalid dimensions.</p>;
  }

  const aspectRatio = usable_width / usable_height;
  
  // Calculate fold positions based on fold type
const getFoldLines = () => {
  if (!foldType) return [];
  
  // FIXED: Use the actual unfolded dimensions passed as props, not the rotated item dimensions
  // unfoldedWidth and unfoldedHeight represent the true job dimensions regardless of sheet orientation
  const actualUnfoldedWidth = unfoldedWidth || item_width_with_bleed;
  const actualUnfoldedHeight = unfoldedHeight || item_height_with_bleed;
  
  const foldLines = [];
  
  // Determine fold direction based on our consistent fold definitions using ACTUAL dimensions
  let actualFoldOrientation = "vertical";
  
  switch(foldType) {
    case 'half':
      // Half fold always folds through the LONG side of the actual unfolded sheet
      // Use the actual unfolded dimensions, not the rotated item dimensions
      actualFoldOrientation = actualUnfoldedWidth >= actualUnfoldedHeight ? "vertical" : "horizontal";
      
      if (actualFoldOrientation === "vertical") {
        foldLines.push({
          direction: 'vertical',
          position: 50, // 50% from left
          label: 'Half Fold'
        });
      } else {
        foldLines.push({
          direction: 'horizontal',
          position: 50, // 50% from top
          label: 'Half Fold'
        });
      }
      break;
      
    case 'parallel':
      // Parallel fold always folds through the SHORT side of the actual unfolded sheet
      actualFoldOrientation = actualUnfoldedWidth >= actualUnfoldedHeight ? "horizontal" : "vertical";
      
      if (actualFoldOrientation === "vertical") {
        foldLines.push({
          direction: 'vertical',
          position: 50, // 50% from left
          label: 'Parallel Fold'
        });
      } else {
        foldLines.push({
          direction: 'horizontal',
          position: 50, // 50% from top
          label: 'Parallel Fold'
        });
      }
      break;
      
    case 'tri':
    case 'z':
      // Tri-fold along the longest dimension of the actual unfolded sheet
      actualFoldOrientation = actualUnfoldedWidth >= actualUnfoldedHeight ? "vertical" : "horizontal";
      
      if (actualFoldOrientation === "vertical") {
        foldLines.push({
          direction: 'vertical',
          position: 33.33, // 1/3 from left
          label: 'Fold 1'
        });
        foldLines.push({
          direction: 'vertical',
          position: 66.67, // 2/3 from left
          label: 'Fold 2'
        });
      } else {
        foldLines.push({
          direction: 'horizontal',
          position: 33.33, // 1/3 from top
          label: 'Fold 1'
        });
        foldLines.push({
          direction: 'horizontal',
          position: 66.67, // 2/3 from top
          label: 'Fold 2'
        });
      }
      break;
      
    case 'gate':
      // Gate fold along the longest dimension of the actual unfolded sheet
      actualFoldOrientation = actualUnfoldedWidth >= actualUnfoldedHeight ? "vertical" : "horizontal";
      
      if (actualFoldOrientation === "vertical") {
        foldLines.push({
          direction: 'vertical',
          position: 25, // 1/4 from left
          label: 'Fold 1'
        });
        foldLines.push({
          direction: 'vertical',
          position: 75, // 3/4 from left
          label: 'Fold 2'
        });
      } else {
        foldLines.push({
          direction: 'horizontal',
          position: 25, // 1/4 from top
          label: 'Fold 1'
        });
        foldLines.push({
          direction: 'horizontal',
          position: 75, // 3/4 from top
          label: 'Fold 2'
        });
      }
      break;
      
    case 'cross':
      // Cross fold - one vertical and one horizontal line in the middle
      foldLines.push({
        direction: 'vertical',
        position: 50, // Middle
        label: 'Fold 1'
      });
      foldLines.push({
        direction: 'horizontal',
        position: 50, // Middle
        label: 'Fold 2'
      });
      break;
      
    case 'accordion':
      // Accordion fold along the longest dimension of the actual unfolded sheet
      actualFoldOrientation = actualUnfoldedWidth >= actualUnfoldedHeight ? "vertical" : "horizontal";
      
      if (actualFoldOrientation === "vertical") {
        foldLines.push({
          direction: 'vertical',
          position: 25, // 1/4 from left
          label: 'Fold 1'
        });
        foldLines.push({
          direction: 'vertical',
          position: 50, // 2/4 from left
          label: 'Fold 2'
        });
        foldLines.push({
          direction: 'vertical',
          position: 75, // 3/4 from left
          label: 'Fold 3'
        });
      } else {
        foldLines.push({
          direction: 'horizontal',
          position: 25, // 1/4 from top
          label: 'Fold 1'
        });
        foldLines.push({
          direction: 'horizontal',
          position: 50, // 2/4 from top
          label: 'Fold 2'
        });
        foldLines.push({
          direction: 'horizontal',
          position: 75, // 3/4 from top
          label: 'Fold 3'
        });
      }
      break;
      
    default:
      // No fold lines for unknown fold types
      break;
  }
  
  return foldLines;
};
  
  const otherFoldLines = getFoldLines();
  const hasOtherFolds = otherFoldLines.length > 0;

  // --- UPDATED: Saddle Stitch Fold Direction Logic ---
  let saddleStitchFoldDirection = 'none'; // Default
  if (isSaddleStitch && impositionData.orientation) {
      // If spreads are arranged in 'landscape' orientation on sheet, fold horizontally
      // If spreads are arranged in 'portrait' orientation on sheet, fold vertically  
      saddleStitchFoldDirection = impositionData.orientation === 'landscape' ? 'horizontal' : 'vertical';
      console.log(`Saddle Stitch: Spreads arranged ${impositionData.orientation} → Fold ${saddleStitchFoldDirection}`);
  }
  // --- END UPDATED ---
  console.log("--- ImpositionVisualizer ---");
  console.log("Final Page (W x H):", finalWidth, finalHeight);
  console.log("Is Saddle Stitch:", isSaddleStitch);
  console.log("Calculated Fold Direction:", saddleStitchFoldDirection);
  console.log("Imposition Data Received:", impositionData);
  return (
    <div className="mt-1">
      {title && <h6 className="text-xs font-medium text-gray-600 mb-1">
        {title} ({items_per_sheet}-up)
        {isSaddleStitch && <span className="ml-1 text-purple-600">(Saddle Stitch Spread)</span>}
        {hasOtherFolds && <span className="ml-1 text-blue-600">with {foldType} fold</span>}
        {hasOtherFolds && unfoldedWidth > 0 && unfoldedHeight > 0 && 
          <span className="ml-1 text-xs text-gray-500">
            (Flat size: {Math.round(unfoldedWidth)}×{Math.round(unfoldedHeight)}mm)
          </span>
        }
      </h6>}
      <div
        className="relative border border-gray-300 bg-white overflow-hidden"
        style={{
          width: '100%', // Take full width of parent
          maxWidth: maxWidth, // Apply max width constraint
          margin: 'auto', // Center if parent allows
          aspectRatio: aspectRatio // Maintain correct aspect ratio based on usable area
        }}
      >
        {/* Items grid */}
        {Array.from({ length: cols * rows }).map((_, idx) => {
          // Only render items that actually fit
          if (idx >= items_per_sheet) return null;

          const col = idx % cols;
          const row = Math.floor(idx / cols);

          // Determine the visual dimensions based on the orientation the backend chose
          const spreadWidth = orientation === 'landscape' ? item_height_with_bleed : item_width_with_bleed;
          const spreadHeight = orientation === 'landscape' ? item_width_with_bleed : item_height_with_bleed;

          // Calculate position relative to the top-left of the usable area
          const leftPos = col * (spreadWidth + gutter_mm);
          const topPos = row * (spreadHeight + gutter_mm);

          // Convert position and size to percentages of the usable area
          const leftPercent = (leftPos / usable_width) * 100;
          const topPercent = (topPos / usable_height) * 100;
          const widthPercent = (spreadWidth / usable_width) * 100;
          const heightPercent = (spreadHeight / usable_height) * 100;

          // Basic validation for percentages
          if (widthPercent <= 0 || heightPercent <= 0 || leftPercent < 0 || topPercent < 0 || leftPercent + widthPercent > 100.1 || topPercent + heightPercent > 100.1) {
            console.warn("Skipping rendering item due to invalid percentage dimensions/position:", idx);
            return null;
          }
          // --- SECOND GROUP OF LOGS (Correct placement inside map, before return) ---
          console.log(`Item ${idx}: Spread Orientation on Sheet: ${orientation}`);
          console.log(`Item ${idx}: Spread Dims (W x H, from data): ${item_width_with_bleed} x ${item_height_with_bleed}`);
          console.log(`Item ${idx}: Drawn Spread Box Dims (W x H): ${spreadWidth} x ${spreadHeight}`);
          // --- END SECOND GROUP ---
          return (
            <div key={idx}
                 className="absolute bg-blue-100 border border-blue-400 box-border"
                 style={{
                   left: `${leftPercent}%`,
                   top: `${topPercent}%`,
                   width: `${widthPercent}%`,
                   height: `${heightPercent}%`
                 }}>
              {/* Render fold lines for each item */}
              {hasOtherFolds && otherFoldLines.map((fold, foldIndex) => {
                if (fold.direction === 'vertical') {
                  return (
                    <div 
                      key={`fold-${idx}-${foldIndex}`}
                      className="absolute top-0 bottom-0 border-l-2 border-dashed border-red-500"
                      style={{
                        left: `${fold.position}%`,
                        zIndex: 10
                      }}
                      title={fold.label}
                    />
                  );
                } else if (fold.direction === 'horizontal') {
                  return (
                    <div 
                      key={`fold-${idx}-${foldIndex}`}
                      className="absolute left-0 right-0 border-t-2 border-dashed border-red-500"
                      style={{
                        top: `${fold.position}%`,
                        zIndex: 10
                      }}
                      title={fold.label}
                    />
                  );
                }
                return null;
              })}
              {/* *** NEW: Render SADDLE STITCH fold line *** */}
              {isSaddleStitch && ( // Use the prop here
                <div
                  className="absolute border-dashed border-purple-600" // Use a different color for saddle stitch
                  style={
                    saddleStitchFoldDirection === 'vertical'
                      ? { top: 0, bottom: 0, left: '50%', borderLeftWidth: '2px', zIndex: 11 } // Vertical line
                      : { left: 0, right: 0, top: '50%', borderTopWidth: '2px', zIndex: 11 } // Horizontal line
                  }
                  title="Saddle Stitch Fold"
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end mt-1 text-xs text-gray-600 space-x-3">
        {/* Add legend for saddle stitch */}
        {isSaddleStitch && (
          <div className="flex items-center">
            <span className="inline-block w-4 h-0 border-t-2 border-dashed border-purple-600 mr-1"></span>
            <span>Saddle Stitch</span>
          </div>
        )}
        {/* Legend for fold lines */}
        {hasOtherFolds && (
          <div className="flex items-center justify-end mt-1 text-xs text-gray-600">
            <span className="inline-block w-4 h-0 border-t-2 border-dashed border-red-500 mr-1"></span>
            <span>Other Fold</span>
          </div>
        )}
      </div>
      {/* --- NEW: Display Textual Folding Details (if provided) --- */}
      {foldingDetailsData && (
        <div className="mt-4 p-3 bg-blue-50 rounded text-sm border border-blue-200">
          <h6 className="font-semibold mb-2">Folding Details</h6>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <strong>Fold Type:</strong> {foldType ? foldType.charAt(0).toUpperCase() + foldType.slice(1) + ' Fold' : 'N/A'}
            </div>
            <div>
              <strong>Total Panels:</strong> {foldingDetailsData.total_panels ?? 'N/A'}
            </div>
            <div className="col-span-2">
              <strong>Original Size:</strong> {foldingDetailsData.original_width?.toFixed(1) ?? 'N/A'}mm x {foldingDetailsData.original_height?.toFixed(1) ?? 'N/A'}mm
            </div>
             <div className="col-span-2">
              <strong>Finished Size:</strong> {foldingDetailsData.final_width?.toFixed(1) ?? 'N/A'}mm x {foldingDetailsData.final_height?.toFixed(1) ?? 'N/A'}mm
            </div>
          </div>
        </div>
      )}
      {/* --- END NEW --- */}
     </div> 
    );
  };
export default ImpositionVisualizer;